import { useEffect, useState } from "react";
import api from "../api/api";
import {
  Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Button, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Tooltip
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import EditIcon   from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../components/AuthContext";

const ROWS_OPTIONS = [10, 25, 50];

export default function Areas() {
  const { hasPermission } = useAuth();
  const [data, setData]             = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit]     = useState(false);
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState({ nombre_area: "" });
  const [pagina, setPagina]         = useState(0);
  const [rowsPorPagina, setRowsPorPagina] = useState(10);

  const cargar = async () => {
    try {
      const res = await api.get("/areas");
      setData(res.data);
      setPagina(0);
    } catch (error) {
      console.error("Error loading areas:", error);
    }
  };

  useEffect(() => { cargar(); }, []);

  const handleCreate = async () => {
    if (!form.nombre_area.trim()) return alert("Enter the area name");
    try {
      await api.post("/areas", form);
      setOpenCreate(false);
      setForm({ nombre_area: "" });
      cargar();
    } catch (error) {
      alert("Error creating area: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = async () => {
    if (!form.nombre_area.trim()) return alert("Enter the area name");
    try {
      await api.put(`/areas/${selected.id_area}`, form);
      setOpenEdit(false);
      setSelected(null);
      setForm({ nombre_area: "" });
      cargar();
    } catch (error) {
      alert("Error updating area: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this area?")) return;
    try {
      await api.delete(`/areas/${id}`);
      cargar();
    } catch (error) {
      alert("Error deleting area: " + (error.response?.data?.detail || error.message));
    }
  };

  const openEditDialog = (area) => {
    setSelected(area);
    setForm({ nombre_area: area.nombre_area });
    setOpenEdit(true);
  };

  const paginados = data.slice(pagina * rowsPorPagina, (pagina + 1) * rowsPorPagina);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Areas</Typography>
        {hasPermission("AREAS_CREAR") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ nombre_area: "" }); setOpenCreate(true); }}>
            Create Area
          </Button>
        )}
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Area Name</TableCell>
            <TableCell sx={{ color: "white" }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginados.map(area => (
            <TableRow key={area.id_area} hover>
              <TableCell>{area.nombre_area}</TableCell>
              <TableCell align="right">
                {hasPermission("AREAS_EDITAR") && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEditDialog(area)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {hasPermission("AREAS_ELIMINAR") && (
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(area.id_area)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow>
              <TableCell colSpan={2} align="center" sx={{ color: "text.secondary", py: 4 }}>
                No areas registered
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {data.length > 0 && (
        <TablePagination
          component="div"
          count={data.length}
          page={pagina}
          onPageChange={(_, p) => setPagina(p)}
          rowsPerPage={rowsPorPagina}
          onRowsPerPageChange={e => { setRowsPorPagina(parseInt(e.target.value)); setPagina(0); }}
          rowsPerPageOptions={ROWS_OPTIONS}
          labelRowsPerPage="Rows:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      )}

      {/* Create */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Area</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Area Name" value={form.nombre_area}
            onChange={e => setForm({ nombre_area: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Area</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Area Name" value={form.nombre_area}
            onChange={e => setForm({ nombre_area: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>Update</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
